import type { ReportListItem } from '../../../utils/reportsClient'
import type { PqrWeldOption } from '../pqrPreview/PqrWeldPickerModal'

/**
 * Pure grouping utility for `PqrReportHistory`. Given the flat report
 * list (already filtered to live + PQR-tagged by the caller) and the
 * full weld option list (pulled from `floorsheet.welds`), produces a
 * three-level structure:
 *
 *   weldGroups[]                — one per surviving weld scope, sorted
 *                                  numerically by weldNumber (welds
 *                                  without numbers sort last; ties on
 *                                  numbers fall through to weldId)
 *     .pqrGroups[]              — one per distinct PQR No. used on the
 *                                  weld, sorted by latest-uploadedAt
 *                                  desc (so the most-recently-touched
 *                                  PQR No. surfaces first)
 *       .reports[]              — revisions in the PQR No. train,
 *                                  sorted by revision desc; the first
 *                                  entry is marked `isLatest: true`
 *
 *   aggregated                  — one bucket for reports without a
 *                                  `weldId` (legacy uploads where the
 *                                  filename parser couldn't extract
 *                                  one). `null` when no such reports
 *                                  exist (so the UI can omit the
 *                                  section entirely).
 *
 * Soft-deleted reports are excluded upstream; this util defensively
 * filters them too so misuse can't leak ghost rows into the UI.
 *
 * Added in `pqr-report-history-and-rev` slice 04.
 */
export const AGGREGATED_GROUP_ID = '__aggregated__'

export interface PqrHistoryReport extends ReportListItem {
  /**
   * True only for the synthetic next-version placeholder at the top of
   * each PQR No. train. Real generated reports always have `isLatest:
   * false` under the always-on synthetic model — the user-facing
   * "Latest" row is always the placeholder, and `Save & Generate
   * Report` inside the preview produces a fresh `ReportListItem` that
   * slides into the older list below while the synthetic bumps to
   * `v(N+1)` automatically.
   */
  isLatest: boolean
  /**
   * True when this row is a UI-only "next version" placeholder
   * computed client-side from `max(real revs) + 1`. The row reuses the
   * exact same chrome as a real row (RevPill, Latest tag, name slot)
   * but carries no PDF metadata — the renderer suppresses the
   * `uploadedAt` / `by <email>` line and the action area shows only
   * the Preview button (no Download / no Remove).
   *
   * Never written to the DB. See the "no-draft-report contract" in
   * `.cursor/plans/pqr_draft_row_*.plan.md`.
   */
  isSynthetic?: boolean
}

export interface PqrHistoryPqrGroup {
  pqrNumber: string
  reports: PqrHistoryReport[]
}

export interface PqrHistoryWeldGroup {
  /**
   * Stable id for React keys. For a single-weld train this equals
   * `weldId`; for a multi-weld train it's the sorted weld-id list
   * joined with '|' (e.g. `"A|B|C"`). The aggregated bucket carries
   * `AGGREGATED_GROUP_ID`.
   */
  id: string
  /**
   * First (primary) weld id in the train. Equals `weldIds[0]` after
   * sorting. Kept for back-compat with code that hasn't migrated to
   * `weldIds`.
   */
  weldId: string
  /**
   * Full sorted weld-id list owning this train (added in
   * `multi-weld-pqr`). Single-weld trains have `[weldId]`; multi-weld
   * trains list every picked weld in sorted order.
   */
  weldIds: string[]
  /**
   * Primary weld number — empty when the weld doesn't appear in
   * `weldOptions`.
   */
  weldNumber: string
  /**
   * Per-weld numbers in `weldIds` order. Empty strings for welds
   * without a stored weld number. Drives the chip-list rendering in
   * the train header.
   */
  weldNumbers: string[]
  versionCount: number
  pqrGroups: PqrHistoryPqrGroup[]
}

export interface GroupedHistory {
  weldGroups: PqrHistoryWeldGroup[]
  aggregated: PqrHistoryWeldGroup | null
}

export interface GroupReportsInput {
  reports: ReportListItem[]
  weldOptions: PqrWeldOption[]
}

function isLive(report: ReportListItem): boolean {
  return !(typeof report.deletedAt === 'string' && report.deletedAt.trim().length > 0)
}

function asRevNumber(rev: string | undefined): number {
  if (typeof rev !== 'string') return 0
  const t = rev.trim()
  if (!/^\d+$/.test(t)) return 0
  return parseInt(t, 10)
}

/**
 * Compares two weldNumber strings numerically. Empty strings sort
 * AFTER any numeric weldNumber (Infinity stand-in).
 */
function compareWeldNumber(a: string, b: string): number {
  const aBlank = !a || a.length === 0
  const bBlank = !b || b.length === 0
  if (aBlank && bBlank) return 0
  if (aBlank) return 1
  if (bBlank) return -1
  return a.localeCompare(b, undefined, { numeric: true })
}

/**
 * Compute the max generated revision number across a set of real
 * reports. Returns `-1` when the set is empty so callers can produce
 * `"0"` as the synthetic-row label via `max + 1`.
 */
function maxRevNumber(items: ReportListItem[]): number {
  let max = -1
  for (const r of items) {
    const n = asRevNumber(r.revision)
    if (n > max) max = n
  }
  return max
}

/**
 * Build a synthetic next-version placeholder for a specific PQR No.
 * train within a specific weld. The row is shaped like a real
 * `ReportListItem` so the existing `ReportRow` renderer can display
 * it without conditionals on the type itself — only the
 * `isSynthetic` flag triggers two small presentational adjustments
 * (no upload metadata, Preview-only actions).
 *
 * `revision` is `max(real revs) + 1`, so a fresh train reads as
 * `v0` and bumps to `v1`, `v2`, ... after each `Save & Generate
 * Report`. `name` mirrors `pqrNumber` because there's no PDF filename
 * for a not-yet-generated version — the renderer otherwise leaves a
 * blank slot.
 */
function buildSyntheticPlaceholderForTrain(
  weldIds: string[],
  pqrNumber: string,
  maxRev: number
): PqrHistoryReport {
  const trainKey = weldIds.join('|')
  return {
    id: `synth-${trainKey}-${pqrNumber}`,
    name: pqrNumber,
    tag: 'PQR',
    fileKey: '',
    uploadedAt: '',
    weldId: weldIds[0] ?? '',
    weldIds,
    pqrNumber,
    revision: String(maxRev + 1),
    isLatest: true,
    isSynthetic: true
  }
}

/**
 * Group a set of reports into per-`pqrNumber` trains.
 *
 * For weld-scoped trains (`options.synthWeldId` provided): every train
 * is prepended with a synthetic next-version placeholder at index `[0]`
 * and every real report inside the train is forced to `isLatest:
 * false`. This is the always-on Preview entry-point model.
 *
 * For the aggregated bucket (no `synthWeldId`): legacy semantics — no
 * synthetic, and the highest-rev real report carries `isLatest: true`.
 * Aggregated reports pre-date weld scoping so they have no `weldId`
 * for a preview URL to target, hence no synthesis.
 */
function buildPqrGroups(
  forTrain: ReportListItem[],
  options: { synthWeldIds?: string[] } = {}
): PqrHistoryPqrGroup[] {
  const byPqrNumber = new Map<string, ReportListItem[]>()
  for (const r of forTrain) {
    const key =
      typeof r.pqrNumber === 'string' && r.pqrNumber.length > 0 ? r.pqrNumber : '(unknown)'
    const arr = byPqrNumber.get(key) ?? []
    arr.push(r)
    byPqrNumber.set(key, arr)
  }
  const synthWeldIds = options.synthWeldIds
  const groups: PqrHistoryPqrGroup[] = []
  Array.from(byPqrNumber.entries()).forEach(([pqrNumber, items]) => {
    const sorted = [...items].sort(
      (a, b) => asRevNumber(b.revision) - asRevNumber(a.revision)
    )
    if (synthWeldIds !== undefined) {
      const realReports: PqrHistoryReport[] = sorted.map((r) => ({
        ...r,
        isLatest: false
      }))
      const synth = buildSyntheticPlaceholderForTrain(
        synthWeldIds,
        pqrNumber,
        maxRevNumber(items)
      )
      groups.push({ pqrNumber, reports: [synth, ...realReports] })
    } else {
      const reports: PqrHistoryReport[] = sorted.map((r, i) => ({
        ...r,
        isLatest: i === 0
      }))
      groups.push({ pqrNumber, reports })
    }
  })
  groups.sort((a, b) => {
    const realA = synthWeldIds !== undefined ? a.reports[1] : a.reports[0]
    const realB = synthWeldIds !== undefined ? b.reports[1] : b.reports[0]
    const aLatest = realA?.uploadedAt || ''
    const bLatest = realB?.uploadedAt || ''
    return bLatest.localeCompare(aLatest)
  })
  return groups
}

/**
 * Pull the report's weld-set in sorted order, preferring the new
 * `weldIds[]` field and falling back to the legacy single `weldId`.
 * Returns `[]` when neither is populated (aggregated bucket).
 */
function reportWeldSetSorted(r: ReportListItem): string[] {
  const raw = Array.isArray(r.weldIds) && r.weldIds.length > 0
    ? r.weldIds
    : typeof r.weldId === 'string' && r.weldId.length > 0
      ? [r.weldId]
      : []
  return [...raw].map((s) => s.trim()).filter(Boolean).sort()
}

export function groupReportsForHistory(input: GroupReportsInput): GroupedHistory {
  const live = input.reports.filter(isLive)
  const weldNumberByWeldId = new Map<string, string>()
  for (const w of input.weldOptions) {
    weldNumberByWeldId.set(w.weldId, w.weldNumber || '')
  }

  // Train identity: sorted weld-set joined by '|'. Multi-weld reports
  // (those carrying `weldIds[]`) get their own train; legacy single-
  // weld reports (only `weldId`) collapse to a 1-element set.
  const byTrain = new Map<string, { weldIds: string[]; items: ReportListItem[] }>()
  const aggregatedItems: ReportListItem[] = []
  for (const r of live) {
    const weldIds = reportWeldSetSorted(r)
    if (weldIds.length === 0) {
      aggregatedItems.push(r)
      continue
    }
    const key = weldIds.join('|')
    const bucket = byTrain.get(key) ?? { weldIds, items: [] }
    bucket.items.push(r)
    byTrain.set(key, bucket)
  }

  // Floorsheet welds with zero generated reports still need a group
  // so the user can land in the preview for them. Seed single-weld
  // buckets here; multi-weld trains only materialize after the user
  // actually generates a multi-weld PQR.
  for (const w of input.weldOptions) {
    if (!w.weldId) continue
    if (!byTrain.has(w.weldId)) {
      byTrain.set(w.weldId, { weldIds: [w.weldId], items: [] })
    }
  }

  const weldGroups: PqrHistoryWeldGroup[] = []
  Array.from(byTrain.entries()).forEach(([id, bucket]) => {
    const weldIds = bucket.weldIds
    const primaryWeldId = weldIds[0] ?? ''
    const weldNumber = weldNumberByWeldId.get(primaryWeldId) ?? ''
    const weldNumbers = weldIds.map((wid) => weldNumberByWeldId.get(wid) ?? '')
    let pqrGroups = buildPqrGroups(bucket.items, { synthWeldIds: weldIds })
    if (pqrGroups.length === 0) {
      // No real reports for this train; seed a single PQR-001 train
      // so the weld(set) still surfaces an entry-point synthetic.
      const synth = buildSyntheticPlaceholderForTrain(weldIds, 'PQR-001', -1)
      pqrGroups = [{ pqrNumber: 'PQR-001', reports: [synth] }]
    }
    weldGroups.push({
      id,
      weldId: primaryWeldId,
      weldIds,
      weldNumber,
      weldNumbers,
      versionCount: bucket.items.length,
      pqrGroups
    })
  })
  // Sort by primary weld number first (so single-weld trains lead),
  // then by id (lexicographic) so multi-weld trains stay grouped near
  // their primary weld and ties are deterministic.
  weldGroups.sort((a, b) => {
    const byNum = compareWeldNumber(a.weldNumber, b.weldNumber)
    if (byNum !== 0) return byNum
    if (a.weldIds.length !== b.weldIds.length) return a.weldIds.length - b.weldIds.length
    return a.id.localeCompare(b.id)
  })

  const aggregated: PqrHistoryWeldGroup | null =
    aggregatedItems.length === 0
      ? null
      : {
          id: AGGREGATED_GROUP_ID,
          weldId: '',
          weldIds: [],
          weldNumber: '',
          weldNumbers: [],
          versionCount: aggregatedItems.length,
          pqrGroups: buildPqrGroups(aggregatedItems)
        }

  return { weldGroups, aggregated }
}
